import { Client } from '@stomp/stompjs';
import { useEffect, useRef } from 'react';
import { getCrmChatSockJsUrl } from '@/config/chat-ws';
import { buildSessionUserTopic, parseSessionEvent } from '@/shared/model/session-event.model';
import { readStompConnectHeaders } from '@/shared/ws/crmStompTransport';
import { attachCrmStompTokenLifecycle, createCrmStompLifecycleRefs, createCrmStompWebSocketFactory, handleCrmStompAuthFailure, } from '@/shared/ws/crmStompTokenLifecycle';
/**
 * Subscribes to `/topic/session.user.{username}` so users learn when a supervisor force-ends their session.
 */
export function useSessionUserTopicSubscription({ username, enabled = true, sockJsUrl = getCrmChatSockJsUrl(), onForceLogout, }) {
    const onForceLogoutRef = useRef(onForceLogout);
    onForceLogoutRef.current = onForceLogout;
    useEffect(() => {
        const trimmed = username?.trim() ?? '';
        if (!enabled || !trimmed)
            return undefined;
        let topic;
        try {
            topic = buildSessionUserTopic(trimmed);
        }
        catch {
            return undefined;
        }
        const lifecycleRefs = createCrmStompLifecycleRefs();
        const stopReconnecting = (reason) => {
            if (lifecycleRefs.stopped.current)
                return;
            lifecycleRefs.stopped.current = true;
            client.reconnectDelay = 0;
            console.warn('[Session STOMP]', reason);
            void client.deactivate().catch(() => undefined);
        };
        const client = new Client({
            webSocketFactory: createCrmStompWebSocketFactory(sockJsUrl, () => {
                client.connectHeaders = readStompConnectHeaders();
            }),
            connectHeaders: readStompConnectHeaders(),
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            debug: () => undefined,
        });
        client.onConnect = () => {
            lifecycleRefs.failureCount.current = 0;
            client.subscribe(topic, (frame) => {
                const event = parseSessionEvent(frame.body);
                if (!event || event.eventType !== 'FORCE_LOGOUT')
                    return;
                onForceLogoutRef.current?.({
                    forcedBy: event.forcedBy,
                    username: event.username,
                });
            });
        };
        client.onStompError = (frame) => {
            const body = frame.headers?.message ?? frame.body ?? '';
            if (/401|unauthorized|authentication/i.test(body)) {
                void handleCrmStompAuthFailure(client, lifecycleRefs, stopReconnecting);
                return;
            }
            lifecycleRefs.failureCount.current += 1;
            if (lifecycleRefs.failureCount.current >= 3) {
                stopReconnecting('Session STOMP errors persisted — subscription stopped.');
            }
            else {
                console.warn('[Session STOMP] broker error', body);
            }
        };
        client.onWebSocketClose = () => {
            if (lifecycleRefs.stopped.current)
                return;
            lifecycleRefs.failureCount.current += 1;
            if (lifecycleRefs.failureCount.current >= 3) {
                void handleCrmStompAuthFailure(client, lifecycleRefs, stopReconnecting);
            }
        };
        client.onWebSocketError = () => {
            if (lifecycleRefs.stopped.current)
                return;
            lifecycleRefs.failureCount.current += 1;
            if (lifecycleRefs.failureCount.current >= 2) {
                void handleCrmStompAuthFailure(client, lifecycleRefs, stopReconnecting);
            }
        };
        try {
            client.activate();
        }
        catch (e) {
            console.warn('[Session STOMP] activate failed', e);
        }
        const detachTokenLifecycle = attachCrmStompTokenLifecycle(client, lifecycleRefs);
        return () => {
            lifecycleRefs.stopped.current = true;
            client.reconnectDelay = 0;
            detachTokenLifecycle();
            void client.deactivate().catch(() => undefined);
        };
    }, [enabled, sockJsUrl, username]);
}
